/**
 * Questionnaire API client
 * Requirements: 1.1 - Fetch published question sets
 */
import { createAuthenticatedClient } from '../../../shared/api/axiosClient';
import type {
  MyColleaguesResponse,
  MyDepartmentsResponse,
  PublishedQuestionSetSummary,
  QuestionSet,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      // Handle unauthorized - redirect to login
      window.location.href = '/login';
    });
  }
  return apiClient;
};

/**
 * Fetch all published question sets
 */
export const fetchQuestionSets = async (): Promise<PublishedQuestionSetSummary[]> => {
  const client = getApiClient();
  const response = await client.get<PublishedQuestionSetSummary[]>('/question-sets');
  return response.data;
};

/**
 * Fetch a specific question set with questions
 */
export const fetchQuestionSetById = async (id: string): Promise<QuestionSet> => {
  const client = getApiClient();
  const response = await client.get<QuestionSet>(`/question-sets/${id}`);
  return response.data;
};

/**
 * Start a new answer session
 */
export const startSession = async (questionSetId: string): Promise<{ sessionId: string }> => {
  const client = getApiClient();
  const response = await client.post('/user-sessions', { questionSetId });
  return response.data;
};

/**
 * Submit an answer to a question (single)
 */
export const submitAnswer = async (
  sessionId: string,
  questionId: string,
  answerChoiceId: string
): Promise<{ answerId: string; progress: { answered: number; total: number } }> => {
  const client = getApiClient();
  const response = await client.post(`/user-sessions/${sessionId}/answers`, {
    questionId,
    answerChoiceId,
  });
  return response.data;
};

/**
 * Submit multiple answers in bulk (optimized, legacy binary-only)
 */
export const submitAnswersBulk = async (
  sessionId: string,
  answers: Array<{ questionId: string; answerChoiceId: string }>
): Promise<{
  sessionId: string;
  savedCount: number;
  skippedCount: number;
  answers: Array<{ questionId: string; answerChoiceId: string; status: 'saved' | 'skipped' }>;
}> => {
  const client = getApiClient();
  const response = await client.post(`/user-sessions/${sessionId}/answers/bulk`, {
    answers,
  });
  console.log('[questionnaireApi] submitAnswersBulk response:', response.data);
  return response.data;
};

/**
 * Submit multiple answers in bulk (multi-format: binary/choice/scale/matrix)
 * binary/choice: answerChoiceId required
 * scale: scaleValue required (1-5)
 * matrix: departmentId + scaleValue required (1-4)
 */
export const submitMultiFormatAnswersBulk = async (
  sessionId: string,
  answers: Array<{
    questionId: string;
    answerChoiceId?: string | null;
    scaleValue?: number | null;
    departmentId?: string | null;
  }>
): Promise<{
  sessionId: string;
  savedCount: number;
  skippedCount: number;
}> => {
  const client = getApiClient();
  const response = await client.post(`/user-sessions/${sessionId}/answers/multi-format`, {
    answers,
  });
  console.log('[questionnaireApi] submitMultiFormatAnswersBulk response:', response.data);
  return response.data;
};

/**
 * Complete a session and get analysis result
 */
export const completeSession = async (
  sessionId: string
): Promise<{
  sessionId: string;
  completedAt: string;
  totalAnswers: number;
  analysisResultId?: string;
}> => {
  const client = getApiClient();
  const response = await client.post(`/user-sessions/${sessionId}/complete`);
  console.log('[questionnaireApi] completeSession response:', response.data);
  return response.data;
};

/**
 * Fetch departments for the questionnaire respondent (Part6 部門間マトリクス用)
 * 自社の全部門から回答者の primary department を除外した一覧を返す
 */
export const fetchMyDepartments = async (): Promise<MyDepartmentsResponse> => {
  const client = getApiClient();
  const response = await client.get<MyDepartmentsResponse>('/my/departments');
  return response.data;
};

/**
 * ネームジェネレータ用: 自分の主所属部署の同僚一覧を取得 (GET /my/colleagues)
 * §03つながりの構造 レベルB の相談相手選択に使う。実名で返る（選択のため）。
 */
export const fetchMyColleagues = async (): Promise<MyColleaguesResponse> => {
  const client = getApiClient();
  const response = await client.get<MyColleaguesResponse>('/my/colleagues');
  return response.data;
};

/**
 * ネームジェネレータで選んだ相談相手(最大3名)を保存 (POST /my/consult-selections)
 * userSessionId は省略（回答者ごとに最新の選択で上書き＝相談ネットワークに適切）。
 */
export const saveConsultSelections = async (targetUserIds: string[]): Promise<void> => {
  const client = getApiClient();
  await client.post('/my/consult-selections', { targetUserIds });
};

/**
 * Get session detail with results
 */
export const getSessionDetail = async (sessionId: string) => {
  const client = getApiClient();
  const response = await client.get(`/user-sessions/${sessionId}`);
  return response.data;
};
